<template>
  <nav class="compliance-spoke-nav" aria-label="Compliance">
    <div class="spoke-inner">
      <router-link
        v-for="item in primaryItems"
        :key="item.to"
        :to="item.to"
        class="spoke-link"
        :class="{ active: isActive(item) }"
      >
        <i :class="item.icon"></i>
        <span>{{ item.label }}</span>
      </router-link>

      <!-- Reports cluster: keep existing report mass here (not global rail) -->
      <div class="spoke-dropdown" :class="{ open: reportsOpen }" @keydown.escape="reportsOpen = false">
        <button
          type="button"
          class="spoke-link spoke-dropdown-trigger"
          :class="{ active: isReportsActive }"
          aria-haspopup="true"
          :aria-expanded="reportsOpen"
          @click="reportsOpen = !reportsOpen"
        >
          <i class="fas fa-chart-bar"></i>
          <span>Reports</span>
          <i class="fas fa-chevron-down chevron"></i>
        </button>
        <div v-show="reportsOpen" class="spoke-menu" role="menu">
          <router-link
            v-for="report in reportItems"
            :key="report.to"
            :to="report.to"
            class="spoke-menu-item"
            role="menuitem"
            @click="reportsOpen = false"
          >
            <i :class="report.icon"></i>
            <span>{{ report.label }}</span>
            <span v-if="report.hint" class="spoke-hint">{{ report.hint }}</span>
          </router-link>
        </div>
      </div>

      <router-link
        v-for="item in trailingItems"
        :key="item.to"
        :to="item.to"
        class="spoke-link"
        :class="{ active: isActive(item) }"
      >
        <i :class="item.icon"></i>
        <span>{{ item.label }}</span>
      </router-link>
    </div>
  </nav>
</template>

<script>
/**
 * Compliance spoke local nav (hub-and-spoke).
 * Reports live here — not in the global sidebar — so we can simplify the
 * rail while keeping report mass for UX (some will sunset / be replaced).
 * @see docs/product/HUB_SPOKE_PORTAL.md
 */
import franchiseHubContext from '@/services/franchiseHubContext';

export default {
  name: 'ComplianceSpokeNav',
  data() {
    return {
      reportsOpen: false,
      franchiseHubEnabled: false,
      franchiseHubProjectId: null,
    };
  },
  computed: {
    primaryItems() {
      const items = [
        {
          to: '/compliance-health',
          label: 'Health',
          icon: 'fas fa-heartbeat',
          match: (path) => path === '/compliance-health' || path.startsWith('/compliance-health/'),
          exclude: (path) => path.startsWith('/compliance-health/consult'),
        },
      ];
      if (this.franchiseHubEnabled && this.franchiseHubProjectId) {
        items.push({
          to: `/projects/${this.franchiseHubProjectId}/franchise-data`,
          label: 'Franchise',
          icon: 'fas fa-store',
          match: (path) => path.includes('/franchise-data'),
        });
      }
      items.push({
        to: '/coi/integrations',
        label: 'Integrations',
        icon: 'fas fa-plug',
        match: (path) => path.startsWith('/coi/integrations'),
      });
      return items;
    },
    reportItems() {
      const items = [
        {
          to: '/compliance-report',
          label: 'Live Compliance Report',
          icon: 'fas fa-chart-bar',
          hint: 'Portfolio',
        },
        {
          to: '/compliance-responsiveness',
          label: 'Responsiveness & Locations',
          icon: 'fas fa-stopwatch',
          hint: 'Ops',
        },
      ];
      // Keep Active Locations as report mass even when hub replaces some flows
      items.push({
        to: '/active-locations-report',
        label: 'Active Locations (FC)',
        icon: 'fas fa-map-marker-alt',
        hint: 'FranConnect',
      });
      return items;
    },
    trailingItems() {
      return [
        {
          to: '/carrier-intelligence',
          label: 'Carriers',
          icon: 'fas fa-building',
          match: (path) =>
            path.startsWith('/carrier-intelligence') || path.startsWith('/carrier-analytics'),
        },
        {
          to: '/compliance-health/consult',
          label: 'Agent',
          icon: 'fas fa-comments',
          match: (path) => path.startsWith('/compliance-health/consult'),
        },
      ];
    },
    isReportsActive() {
      const path = this.$route.path;
      return this.reportItems.some(
        (r) => path === r.to || path.startsWith(`${r.to}/`),
      );
    },
  },
  watch: {
    $route() {
      this.reportsOpen = false;
    },
  },
  async mounted() {
    document.addEventListener('click', this.onDocClick);
    try {
      const ctx = await franchiseHubContext.load();
      this.franchiseHubEnabled = Boolean(ctx?.hub_enabled || ctx?.visible);
      this.franchiseHubProjectId = ctx?.project_id || null;
    } catch (e) {
      this.franchiseHubEnabled = false;
    }
  },
  beforeUnmount() {
    document.removeEventListener('click', this.onDocClick);
  },
  methods: {
    isActive(item) {
      const path = this.$route.path;
      if (item.exclude && item.exclude(path)) return false;
      if (item.match) return item.match(path);
      return path === item.to || path.startsWith(`${item.to}/`);
    },
    onDocClick(event) {
      if (!this.$el.contains(event.target)) {
        this.reportsOpen = false;
      }
    },
  },
};
</script>

<style scoped>
.compliance-spoke-nav {
  background: var(--p-surface-elevated, #fff);
  border-bottom: 1px solid var(--p-border, #e2e8f0);
  margin: calc(-1 * var(--spacing-lg, 1.5rem)) calc(-1 * var(--spacing-lg, 1.5rem)) var(--spacing-lg, 1.5rem);
  padding: 0.5rem var(--spacing-lg, 1.5rem);
  position: sticky;
  top: 3.5rem; /* sit under AppTopBar */
  z-index: 40;
}

.spoke-inner {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.25rem;
}

.spoke-link {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.45rem 0.75rem;
  border-radius: 0.5rem;
  border: none;
  background: transparent;
  color: var(--p-ink-muted, #64748b);
  font-size: 0.875rem;
  font-weight: 500;
  text-decoration: none;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.spoke-link:hover {
  background: var(--p-surface-muted, #f1f5f9);
  color: var(--p-ink, #1e293b);
}

.spoke-link.active {
  background: var(--p-accent-subtle, #eef2f8);
  color: var(--p-accent, #1e3a5f);
}

.spoke-dropdown {
  position: relative;
}

.chevron {
  font-size: 0.65rem;
  opacity: 0.7;
}

.spoke-menu {
  position: absolute;
  top: calc(100% + 0.25rem);
  left: 0;
  min-width: 16rem;
  padding: 0.35rem;
  background: var(--p-surface-elevated, #fff);
  border: 1px solid var(--p-border, #e2e8f0);
  border-radius: 0.75rem;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
  z-index: 50;
}

.spoke-menu-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.55rem 0.65rem;
  border-radius: 0.5rem;
  color: var(--p-ink, #1e293b);
  text-decoration: none;
  font-size: 0.875rem;
}

.spoke-menu-item:hover {
  background: var(--p-surface-muted, #f1f5f9);
}

.spoke-hint {
  margin-left: auto;
  font-size: 0.7rem;
  color: var(--p-ink-muted, #64748b);
}

@media (max-width: 768px) {
  .compliance-spoke-nav {
    margin-left: 0;
    margin-right: 0;
  }
}
</style>
